import React, { useState } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'
import { Col, Row } from 'react-bootstrap'

import RegItem from 'cart/components/reg-item'
import {
  MultiStepWizard,
  TwoColumnResponsiveLayout,
} from 'components/presentation'
import {
  Checkbox,
  Input,
  Radio,
  RadioGroup,
  RelationshipToBabySelector,
} from 'components/forms'
import { email as isEmail, numeric as isNumeric } from 'lib/form-validation'
import { track, useTracking } from 'lib/analytics'
import { InfoButton, LinkButton, SecondaryButton } from 'components/buttons'
import Link from 'components/link'
import { setGiftGiverCookie, useGiftGiverCookie } from 'lib/cookies'
import { isString } from 'lodash'
import {
  MARKETING_EMAIL_CHECKBOX_LABEL,
  NEW_RESERVATION_ID,
} from 'cart/constants'
import { HELLO_EMAIL, UPDATE_CART_SUMMARY } from 'shared/constants'
import { cashFundPrintablePath } from 'lib/urls'
import ThankYouAddressModal from './modals/ThankYouAddressModal'
import { getIsGiftGiverThankYouAddressProvided } from '../../cart/reducers/reservations-reducer'
import { getReservationsByCurrentVisitor } from '../reducers'
import MailcheckedInput from 'components/mailchecked-input'

const PAYMENT_OPTION_STEP = 1
const RESERVATION_INFO_STEP = 2
const THANK_YOU_ADDRESS_STEP = 3
const CASH_CHECK_INSTRUCTIONS_STEP = 4
const PAYPALME_INSTRUCTIONS_STEP = 5
const CASH = 'CASH'
const PAYPAL = 'PAYPAL'

const CrowdfundReserveForm = ({
  regItem,
  preferences,
  onCreateReservation,
  onUpdateReservation,
  onRemoveReservation,
  onHide,
  isGiftGiverThankYouAddressProvided,
}) => {
  const tracker = useTracking()
  const [activeStep, setActiveStep] = useState(PAYMENT_OPTION_STEP)
  const [paymentOption, setPaymentOption] = useState()
  const [currentReservation, setCurrentReservation] = useState()

  const { name, email, subscribeToMarketingEmail, relationshipToBaby } =
    useGiftGiverCookie(regItem.registryId)

  const { register, handleSubmit, errors, setValue } = useForm({
    defaultValues: {
      name,
      email,
      subscribeToMarketingEmail,
      giftAmount: currentReservation?.giftAmount,
      relationshipToBaby,
    },
  })

  const updateEmail = (eventOrValue) => {
    const value = isString(eventOrValue)
      ? eventOrValue
      : eventOrValue.target.value
    setValue('email', value)
  }

  const handleReset = () => {
    setActiveStep(PAYMENT_OPTION_STEP)
    setPaymentOption(false)
  }

  const handleReservation = ({
    name,
    email,
    giftAmount,
    subscribeToMarketingEmail,
    relationshipToBaby,
  }) => {
    setGiftGiverCookie({
      name,
      email,
      subscribeToMarketingEmail,
      relationshipToBaby,
      registryId: regItem.registryId,
    })

    if (!isGiftGiverThankYouAddressProvided) {
      setActiveStep(THANK_YOU_ADDRESS_STEP)
    } else {
      if (paymentOption === CASH) {
        setActiveStep(CASH_CHECK_INSTRUCTIONS_STEP)
      }
      if (paymentOption === PAYPAL) {
        setActiveStep(PAYPALME_INSTRUCTIONS_STEP)
      }
    }

    const data = {
      id: currentReservation?.id || NEW_RESERVATION_ID,
      regItemId: regItem.id,
      regItem,
      registryId: regItem.registryId,
      name,
      email,
      quantity: 1,
      isPurchased: true,
      subscribeToMarketingEmail,
      relationshipToBaby,
    }

    if (giftAmount) {
      data.giftAmount = giftAmount
    }

    if (currentReservation) {
      onUpdateReservation({ ...currentReservation, ...data })
    } else {
      onCreateReservation(data).then((res) => {
        const { email, userId } = res
        setCurrentReservation(res)
        tracker.trackEvent({
          event: track.emailCaptured,
          eventLocation: track.EventLocation.GIFT_GIVER_MINUS_EMAIL_COLLECTION,
          eventUrl: window.location.href,
          email,
          userId,
        })
      })
    }
  }

  const handleCancelReservation = () => {
    onRemoveReservation(currentReservation).then(() => {
      PubSub.publish(UPDATE_CART_SUMMARY, {
        showCartDropdown: false,
        animateBadge: true,
      })
    })
    onHide()
  }

  const SecondaryActionLinks = (
    <div className="text-center">
      <LinkButton
        block
        className="h7 text-underline mvm pan"
        onClick={handleReset}
      >
        Change my payment option
      </LinkButton>
      <LinkButton
        block
        className="h7 text-underline text-center pan"
        onClick={handleCancelReservation}
      >
        I've changed my mind, cancel this gift
      </LinkButton>
    </div>
  )

  return (
    <MultiStepWizard activeStep={activeStep} animateInitialState={false}>
      {/* Step 1 */}
      <TwoColumnResponsiveLayout>
        <RegItem className="mbl" regItem={regItem} />
        <>
          <RadioGroup className="mbl" title="Payment Options">
            {!!preferences?.direct && (
              <Radio
                name="paymentOption"
                label="I will send cash or check directly"
                tappable
                onChange={() => {
                  setActiveStep(RESERVATION_INFO_STEP)
                  setPaymentOption(CASH)
                }}
              />
            )}
            {!!preferences?.paypalme && (
              <Radio
                name="paymentOption"
                label="Credit / Debit via PayPal"
                tappable
                onChange={() => {
                  setActiveStep(RESERVATION_INFO_STEP)
                  setPaymentOption(PAYPAL)
                }}
              />
            )}
          </RadioGroup>
        </>
      </TwoColumnResponsiveLayout>

      {/* Step 2: Reservation Info */}
      <div>
        <TwoColumnResponsiveLayout>
          <RegItem className="mbl" regItem={regItem} />
          <form onSubmit={handleSubmit(handleReservation)}>
            <p>Provide your name and email so they will know who to thank!</p>
            <div className="mbl">
              <Row>
                <Col xs={12}>
                  <Input
                    label="Name"
                    name="name"
                    type="text"
                    inputRef={register({
                      required: 'Please add your name',
                    })}
                    error={errors.name && errors.name.message}
                  />
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  <MailcheckedInput
                    error={errors?.email?.message}
                    label="Email"
                    placeholder="Your email address"
                    name="email"
                    inputRef={register({
                      required: 'Enter a valid email address',
                      validate: isEmail,
                    })}
                    onChange={updateEmail}
                    mailcheckOnBlur={false}
                    mailcheckOnChange
                    mailcheckDelay={300}
                  />
                </Col>
              </Row>

              <Row>
                <Col xs={12}>
                  <RelationshipToBabySelector
                    inputRef={register}
                    error={errors?.relationshipToBaby?.message}
                  />
                </Col>
              </Row>

              <Row>
                <Col xs={12}>
                  <Input
                    label="Gift Amount"
                    name="giftAmount"
                    help="Optional: Let them know how much you plan to give."
                    inputRef={register({
                      validate: isNumeric,
                    })}
                    error={errors.giftAmount && errors.giftAmount.message}
                  />
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  <Checkbox
                    name="subscribeToMarketingEmail"
                    label={MARKETING_EMAIL_CHECKBOX_LABEL}
                    defaultChecked={subscribeToMarketingEmail}
                    type="checkbox"
                    value="true"
                    inputRef={register()}
                  />
                </Col>
              </Row>
              <InfoButton type="submit" block>
                Next
              </InfoButton>
            </div>
          </form>
        </TwoColumnResponsiveLayout>
      </div>

      {/* Step 3: Thank You Address */}
      <ThankYouAddressModal
        afterSubmit={() => {
          if (paymentOption === CASH) {
            setActiveStep(CASH_CHECK_INSTRUCTIONS_STEP)
          }
          if (paymentOption === PAYPAL) {
            setActiveStep(PAYPALME_INSTRUCTIONS_STEP)
          }
        }}
        giftGiverInfo={{ name, email }}
        onHide={onHide}
      />

      {/* Step 4: Address */}
      <div>
        <TwoColumnResponsiveLayout className="ban">
          <div className="well paxl">
            Bring cash or your check to the baby shower or mail it to them at
            this address.
            <br />
            <br />
            We'll let them know that you plan to give them this gift.
            <br />
            <br />
            Grab the{' '}
            <Link url={cashFundPrintablePath} newWindow>
              Cash Fund printable
            </Link>{' '}
            to bring in person.
          </div>
          <div>
            {preferences?.address ? (
              <address id="address" className="lh-base paxl text-center">
                {preferences.address.name}
                <br />
                {preferences.address.streetAddress1}{' '}
                {preferences.address.streetAddress2 ? (
                  <span>, {preferences.address.streetAddress2}</span>
                ) : null}
                <br />
                {preferences.address.city}, {preferences.address.state}{' '}
                {preferences.address.zip}
              </address>
            ) : (
              <p>
                We don't have a shipping address for this registry. If you need
                it, email us at{' '}
                <a href={`mailto:${HELLO_EMAIL}`}>{HELLO_EMAIL}</a> and we'll
                request it from them.
              </p>
            )}
            <InfoButton block onClick={onHide}>
              I'm finished
            </InfoButton>
          </div>
        </TwoColumnResponsiveLayout>
        {SecondaryActionLinks}
      </div>

      {/* Step 5: Paypal */}
      <div>
        <TwoColumnResponsiveLayout className="ban">
          <div className="text-center">
            <div className="well paxl">
              <p>
                Click the Paypal link below to make a contribution to their cash
                fund.
              </p>
              <SecondaryButton
                className="mtl"
                href={`https://paypal.me/${preferences.paypalme}`}
                pill
                newWindow
              >
                Go to Paypal to Pay
              </SecondaryButton>
              <br />
            </div>
          </div>
          <div>
            <p>We'll let them know that you plan to give them this gift!</p>
            <InfoButton block onClick={onHide}>
              I'm finished
            </InfoButton>
          </div>
        </TwoColumnResponsiveLayout>
        {SecondaryActionLinks}
      </div>
    </MultiStepWizard>
  )
}

CrowdfundReserveForm.propTypes = {
  preferences: PropTypes.shape({
    address: PropTypes.object,
    direct: PropTypes.bool,
    hasPaymentOption: PropTypes.bool,
    paypalme: PropTypes.string,
  }).isRequired,
  regItem: PropTypes.object.isRequired,
  onCreateReservation: PropTypes.func.isRequired,
  onUpdateReservation: PropTypes.func.isRequired,
  onRemoveReservation: PropTypes.func.isRequired,
  onHide: PropTypes.func.isRequired,
  isGiftGiverThankYouAddressProvided: PropTypes.bool.isRequired,
}

const mapStateToProps = (state) => {
  const reservations = Object.values(getReservationsByCurrentVisitor(state))
  return {
    isGiftGiverThankYouAddressProvided:
      getIsGiftGiverThankYouAddressProvided(reservations),
  }
}

export default connect(mapStateToProps)(CrowdfundReserveForm)
